import { LoadingButton } from '@mui/lab'
import { Box, Paper, Step, StepLabel, Stepper, Button } from '@mui/material'
import React, { createContext, useContext, useEffect, useState } from 'react'
import PublishIcon from '@mui/icons-material/Publish'
import Papa from 'papaparse'
import { Modal } from 'Common/Supplyvue-ui'
import DataTabContent from './DataTabContent'
import MappingTabContent from './MappingTabContent'
import UploadTabContent from './UploadTabContent'
import {
  UPLOAD_MODAL_STEPS,
  STEP_LABELS,
  dataTransformationUtils,
} from './constants'
import { GridColDef } from '@mui/x-data-grid'

/**
 * Utility function to check if a row has non-empty values
 * @param row - Data row to check
 * @param excludeId - Whether to exclude the 'id' field from consideration
 * @returns true if the row contains at least one non-empty value
 */
export const hasNonEmptyValues = (
  row: Record<string, unknown>,
  excludeId = false
): boolean => {
  // If the row is not an object, consider it empty
  if (!row || typeof row !== 'object') return false

  // Get all values (optionally excluding 'id')
  const entries = Object.entries(row)
  const values = excludeId
    ? entries.filter(([key]) => key !== 'id').map(([_, val]) => val)
    : entries.map(([_, val]) => val)

  // If no values to check, consider it empty
  if (values.length === 0) return false

  // Return true if at least one value is non-empty
  for (const val of values) {
    if (val !== null && val !== undefined && val !== '') {
      return true
    }
  }
  return false
}

// Define types for the context
type DropZoneContextType = {
  columns: GridColDef[]
  uploadFile: (file: File) => void
  acceptedFiles: Array<File>
  isUploading?: boolean
  tableData: Array<Record<string, unknown>>
  updateRowsStatus?: Record<string, unknown>
  originalData?: Array<Record<string, unknown>>
  predicate?: string[]
  dataConfig?: {
    required: string[]
    optional: string[]
  }
}

// Create the context
const DropZoneContext = createContext<DropZoneContextType | undefined>(
  undefined
)

// Create a hook to use the context
export const useDropZoneContext = () => {
  const context = useContext(DropZoneContext)
  if (context === undefined) {
    throw new Error('useDropZoneContext must be used within a DropZoneProvider')
  }
  return context
}

// We pass original data so that we know all the columns in the original file.
// If data config is passed only required and optional columns are shown in the mapping table.
type DropzoneProps = {
  isUploading?: boolean
  uploadFile: (file: File) => void
  columns: GridColDef[]
  updateRowsStatus?: Record<string, unknown>
  originalData?: Array<Record<string, unknown>>
  predicate?: string[]
  dataConfig?: {
    required: string[]
    optional: string[]
  }
  file?: File | null
}

export const DropZone = (props: DropzoneProps) => {
  const {
    isUploading,
    uploadFile,
    columns,
    updateRowsStatus,
    originalData,
    predicate = [],
    dataConfig = { required: [], optional: [] },
    file,
  } = props

  const [tableData, setTableData] = useState<Array<Record<string, unknown>>>([])
  const [isModalOpen, setIsModalOpen] = useState(false)

  useEffect(() => {
    if (file) {
      if (!file) {
        console.error('No file selected')
        return
      }
      // Use Papa.parse with file object directly
      Papa.parse<Record<string, unknown>>(file, {
        complete: (result: Papa.ParseResult<Record<string, unknown>>) => {
          let parsedData = result.data
          if (parsedData.length === 0) {
            console.error('No data found in the CSV file')
            return
          }

          // Remove empty rows
          parsedData = parsedData.filter((row) => hasNonEmptyValues(row))

          // Add ID to each row
          parsedData = parsedData.map((row, index) => {
            return {
              ...row,
              id: index,
            }
          })

          setTableData(parsedData)
          setIsModalOpen(true)
        },
        error: (error: Error, _file: any) => {
          console.error('CSV parsing failed:', error)
        },
        header: true,
        dynamicTyping: true,
        skipEmptyLines: true, // Skip empty lines in the CSV
        transform: (value: string) => {
          // Handle null-like values first
          if (value === 'null' || value === 'NULL' || value === '') {
            return null
          }
          // Let Papa.parse handle other type conversions
          return value
        },
      })
    }
  }, [file])

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = React.useMemo(
    () => ({
      columns,
      uploadFile,
      tableData,
      acceptedFiles: file ? [file] : [],
      isUploading,
      updateRowsStatus,
      originalData,
      predicate: predicate ?? [],
      dataConfig,
    }),
    [
      columns,
      uploadFile,
      tableData,
      file,
      isUploading,
      updateRowsStatus,
      originalData,
      predicate,
      dataConfig,
    ]
  )

  // Provide the columns to the context
  return (
    <DropZoneContext.Provider value={contextValue}>
      {isModalOpen && (
        <UploadModal
          data={tableData}
          isOpen={isModalOpen}
          handleModalClose={() => setIsModalOpen(false)}
        />
      )}
    </DropZoneContext.Provider>
  )
}

interface UploadModalProps {
  data: Array<Record<string, unknown>>
  isOpen?: boolean
  handleModalClose?: () => void
}

const UploadModal = ({
  data,
  isOpen = false,
  handleModalClose = () => {},
}: UploadModalProps) => {
  return (
    <Modal
      open={isOpen}
      handleClose={handleModalClose}
      header="Upload CSV Data"
      body={<UploadModalBody data={data} handleModalClose={handleModalClose} />}
    />
  )
}

// Tab components moved to their own files

const UploadModalBody = ({
  data, //This is the parsed data from the CSV file
  handleModalClose = () => {},
}: {
  data: Array<Record<string, unknown>>
  handleModalClose?: () => void
}) => {
  // Access context which contains all needed information
  const context = useDropZoneContext()

  // State for active step (replaces selected tab)
  const [activeStep, setActiveStep] = useState(UPLOAD_MODAL_STEPS.DATA_STEP)

  // State for upload validation
  const [isUploadValid, setIsUploadValid] = useState(true)

  // Memoize required target columns calculations
  const requiredTargetColumns = React.useMemo(() => {
    if (context.dataConfig && context.dataConfig.required.length > 0) {
      // Include predicate columns that are also in required
      const predicateInRequired = (context.predicate ?? []).filter((col) =>
        context.dataConfig.required.includes(col)
      )

      return [
        ...new Set([...context.dataConfig.required, ...predicateInRequired]),
      ]
    }

    // Fallback to editable columns for backward compatibility
    const editableColumns = context.columns
      .filter((colDefinition) => colDefinition.editable)
      .map((colDefinition) => colDefinition.field)

    return [...new Set([...(context.predicate ?? []), ...editableColumns])]
  }, [context.dataConfig, context.predicate, context.columns])

  // Memoize optional target columns calculations
  const optionalTargetColumns = React.useMemo(() => {
    if (context.dataConfig && context.dataConfig.optional.length > 0) {
      // Include predicate columns that are also in optional
      const predicateInOptional = (context.predicate ?? []).filter((col) =>
        context.dataConfig.optional.includes(col)
      )

      return [
        ...new Set([...context.dataConfig.optional, ...predicateInOptional]),
      ]
    }

    return []
  }, [context.dataConfig, context.predicate])

  // Combined target columns for backward compatibility in other places
  const _targetColumns = React.useMemo(() => {
    return [...new Set([...requiredTargetColumns, ...optionalTargetColumns])]
  }, [requiredTargetColumns, optionalTargetColumns])

  // Memoize column definitions for displaying data
  const { generatedColumns, mappingColumns } = React.useMemo(() => {
    // Get all available CSV columns (excluding 'id')
    const availableColumns = Object.keys(data[0] || {}).filter(
      (key) => key !== 'id'
    )

    // Determine which columns to show based on dataConfig
    const columnsToShow: string[] = availableColumns

    // Create columns for display based on filtered columns
    const keyColumns: GridColDef[] = columnsToShow.map((key) => ({
      field: key,
      headerName: key,
      flex: 1,
    }))

    // Value options for the source dropdown in mapping table - use the same filtered columns
    const valueOptions = columnsToShow

    // Define mapping columns for the mapping table
    const mappingCols = [
      {
        field: 'target',
        headerName: 'Target (DB columns)',
        flex: 1,
      },
      {
        field: 'source',
        headerName: 'Source (CSV columns)',
        flex: 1,
        type: 'singleSelect' as const,
        editable: true,
        valueOptions: valueOptions,
      },
    ]

    return {
      generatedColumns: [...keyColumns],
      mappingColumns: mappingCols,
    }
  }, [
    data,
    context.columns,
    context.tableData,
    context.dataConfig,
    context.predicate,
  ])

  // Generate initial mapping rows for required columns
  const initialRequiredMappingRows = React.useMemo(() => {
    return requiredTargetColumns.map((col) => ({
      id: col,
      source:
        generatedColumns.find(
          (generatedColumn) => generatedColumn.field === col
        )?.field ?? null,
      target: col,
    }))
  }, [requiredTargetColumns, generatedColumns])

  // Generate initial mapping rows for optional columns
  const initialOptionalMappingRows = React.useMemo(() => {
    return optionalTargetColumns.map((col) => ({
      id: col,
      source:
        generatedColumns.find(
          (generatedColumn) => generatedColumn.field === col
        )?.field ?? null,
      target: col,
    }))
  }, [optionalTargetColumns, generatedColumns])

  // Legacy combined mapping rows for backward compatibility
  const _initialMappingRows = React.useMemo(() => {
    return [...initialRequiredMappingRows, ...initialOptionalMappingRows]
  }, [initialRequiredMappingRows, initialOptionalMappingRows])

  // State to track current mapping rows (to persist across step switches)
  // Define the type explicitly to match the initialMappingRows structure
  type MappingRowType = {
    id: unknown
    source: string | null
    target: unknown
  }
  const [currentRequiredMappingRows, setCurrentRequiredMappingRows] =
    React.useState<MappingRowType[]>(initialRequiredMappingRows)
  const [currentOptionalMappingRows, setCurrentOptionalMappingRows] =
    React.useState<MappingRowType[]>(initialOptionalMappingRows)

  // Legacy combined state for backward compatibility
  const _currentMappingRows = React.useMemo(() => {
    return [...currentRequiredMappingRows, ...currentOptionalMappingRows]
  }, [currentRequiredMappingRows, currentOptionalMappingRows])

  // Store the original mappings in refs to use for reset
  const originalRequiredMappingRows = React.useRef(initialRequiredMappingRows)
  const originalOptionalMappingRows = React.useRef(initialOptionalMappingRows)

  // Validation for required mapping completeness
  const isRequiredMappingComplete = React.useMemo(() => {
    return currentRequiredMappingRows.every(
      (row) =>
        row.source !== null && row.source !== undefined && row.source !== ''
    )
  }, [currentRequiredMappingRows])

  // Get successfully mapped optional rows (ones that have a source mapping)
  const successfullyMappedOptionalRows = React.useMemo(() => {
    return currentOptionalMappingRows.filter(
      (row) =>
        row.source !== null && row.source !== undefined && row.source !== ''
    )
  }, [currentOptionalMappingRows])

  // Combined mapping for transformation (required + successfully mapped optional)
  const combinedMappingForTransformation = React.useMemo(() => {
    return [...currentRequiredMappingRows, ...successfullyMappedOptionalRows]
  }, [currentRequiredMappingRows, successfullyMappedOptionalRows])

  // Handle upload button click
  const handleUploadClick = React.useCallback(() => {
    if (context.acceptedFiles?.[0]) {
      // First, transform the data based on the combined mapping (required + successfully mapped optional)
      const transformedData = transformDataUsingMapping(
        context.tableData,
        combinedMappingForTransformation
      )

      // Create a new File object with the transformed data
      const transformedFile = createFileWithTransformedData(
        transformedData,
        context.acceptedFiles[0].name
      )

      // Upload the transformed file
      context.uploadFile(transformedFile)
    }
  }, [
    context.acceptedFiles,
    context.uploadFile,
    context.tableData,
    combinedMappingForTransformation,
  ])

  // Use the shared utility function for data transformation
  const transformDataUsingMapping = (
    sourceData: Array<Record<string, unknown>>,
    mappingRows: Array<Record<string, unknown>>
  ): Array<Record<string, unknown>> => {
    console.log('Source data:', sourceData)
    console.log('Mapping rows:', mappingRows)

    // Filter out empty rows from source data before transformation
    const filteredSourceData = sourceData.filter((row) =>
      hasNonEmptyValues(row, true)
    )

    const result = dataTransformationUtils.transformDataUsingMapping(
      filteredSourceData,
      mappingRows
    )

    console.log('Target to source mapping result:', result)

    // Additional filter to remove any empty rows that might have been created during transformation
    const filteredResult = result.filter((row) => hasNonEmptyValues(row, true))

    return filteredResult
  }

  // Helper function to create a File object from transformed data
  const createFileWithTransformedData = (
    transformedData: Array<Record<string, unknown>>,
    originalFileName: string
  ): File => {
    // Filter out empty rows before converting to CSV
    const filteredData = transformedData.filter((row) =>
      hasNonEmptyValues(row, true)
    )

    // Convert transformed data back to CSV using Papa.unparse
    const csv = Papa.unparse(filteredData, {
      skipEmptyLines: true, // Additional safety to skip any empty lines
    })

    // Create a new File object
    const blob = new Blob([csv], { type: 'text/csv' })
    return new File([blob], originalFileName, { type: 'text/csv' })
  }

  // Handle required mapping updates
  const handleRequiredMappingUpdate = React.useCallback(
    (updatedRows: Array<Record<string, unknown>>) => {
      console.log('Required mapping updated:', updatedRows)
      setCurrentRequiredMappingRows(updatedRows as MappingRowType[])
    },
    []
  )

  // Handle optional mapping updates
  const handleOptionalMappingUpdate = React.useCallback(
    (updatedRows: Array<Record<string, unknown>>) => {
      console.log('Optional mapping updated:', updatedRows)
      setCurrentOptionalMappingRows(updatedRows as MappingRowType[])
    },
    []
  )

  // Handle mapping reset - restore to original mapping
  const handleMappingReset = React.useCallback(() => {
    console.log('Resetting to original mapping')
    setCurrentRequiredMappingRows([...originalRequiredMappingRows.current])
    setCurrentOptionalMappingRows([...originalOptionalMappingRows.current])
  }, [])

  // Handle validation state changes from UploadTabContent
  const handleValidationChange = React.useCallback((isValid: boolean) => {
    setIsUploadValid(isValid)
  }, [])

  // Handle next step with validation
  const handleNext = () => {
    // If we're moving from mapping step to upload step, validate required mappings
    if (activeStep === UPLOAD_MODAL_STEPS.MAPPING_STEP) {
      if (!isRequiredMappingComplete) {
        console.warn('Cannot proceed: Required mappings are incomplete')
        return // Block navigation if required mappings are incomplete
      }

      console.log(
        'All required mappings complete. Proceeding to upload step with:',
        {
          required: currentRequiredMappingRows.length,
          optional: successfullyMappedOptionalRows.length,
          total: combinedMappingForTransformation.length,
        }
      )
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1)
  }

  // Handle back step
  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1)
  }

  // Get step content based on current step
  const getStepContent = (step: number) => {
    switch (step) {
      case UPLOAD_MODAL_STEPS.DATA_STEP:
        return (
          <DataTabContent data={data} generatedColumns={generatedColumns} />
        )
      case UPLOAD_MODAL_STEPS.MAPPING_STEP:
        return (
          <MappingTabContent
            requiredMappingRows={currentRequiredMappingRows}
            optionalMappingRows={currentOptionalMappingRows}
            mappingColumns={mappingColumns}
            onRequiredMappingUpdate={handleRequiredMappingUpdate}
            onOptionalMappingUpdate={handleOptionalMappingUpdate}
            onMappingReset={handleMappingReset}
          />
        )
      case UPLOAD_MODAL_STEPS.UPLOAD_STEP:
        return (
          <UploadTabContent
            mappingRows={combinedMappingForTransformation}
            handleModalClose={handleModalClose}
            handleUploadClick={handleUploadClick}
            isUploading={context.isUploading}
            onValidationChange={handleValidationChange}
          />
        )
      default:
        return 'Unknown step'
    }
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '20px',
        width: 'calc(100vw - 30vw)',
        height: 'calc(100vh - 30vh)',
        gap: 2,
      }}
    >
      {/* Stepper Navigation */}
      <Box width="100%" sx={{ mb: 2 }}>
        <Paper elevation={1} sx={{ p: 1 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {STEP_LABELS.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Paper>
      </Box>

      {/* Step Content */}
      <Box
        width="100%"
        sx={{
          overflowY: 'auto',
          mb: 3,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {getStepContent(activeStep)}
      </Box>

      {/* Navigation Buttons */}
      {!(
        activeStep === UPLOAD_MODAL_STEPS.UPLOAD_STEP &&
        Boolean(
          context.updateRowsStatus &&
            Object.keys(context.updateRowsStatus).length > 0
        )
      ) && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
            mt: 2,
          }}
        >
          <Box>
            <Button
              variant="outlined"
              disabled={activeStep === 0}
              onClick={handleBack}
            >
              Back
            </Button>
          </Box>

          <Box>
            {activeStep === UPLOAD_MODAL_STEPS.UPLOAD_STEP ? (
              <LoadingButton
                variant="contained"
                color="primary"
                onClick={handleUploadClick}
                loading={context.isUploading}
                startIcon={<PublishIcon />}
                disabled={
                  context.isUploading ||
                  !isUploadValid ||
                  Boolean(
                    context.updateRowsStatus &&
                      Object.keys(context.updateRowsStatus).length > 0
                  )
                }
              >
                Upload
              </LoadingButton>
            ) : (
              <Button
                variant="contained"
                color="primary"
                onClick={handleNext}
                disabled={
                  activeStep === UPLOAD_MODAL_STEPS.MAPPING_STEP &&
                  !isRequiredMappingComplete
                }
              >
                {activeStep === UPLOAD_MODAL_STEPS.MAPPING_STEP &&
                !isRequiredMappingComplete
                  ? 'Map All Required Columns'
                  : 'Next'}
              </Button>
            )}
          </Box>
        </Box>
      )}
    </Box>
  )
}
